import React, { createContext, useContext, useState, useCallback } from 'react';

export type NotificationType = 'pending' | 'transfer';

export interface Notification {
  id: number;
  type: NotificationType;
  contactId: number;
  contactName: string;
  timestamp: number;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (type: NotificationType, contactId: number, contactName: string) => void;
  removeNotification: (id: number) => void;
  removeByContactId: (contactId: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const AUTO_REMOVE_MS = 15000;

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used within NotificationProvider');
  return ctx;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const removeByContactId = useCallback((contactId: number) => {
    setNotifications(prev => prev.filter(n => n.contactId !== contactId));
  }, []);

  const addNotification = useCallback((type: NotificationType, contactId: number, contactName: string) => {
    const id = Date.now();
    const notification: Notification = {
      id,
      type,
      contactId,
      contactName,
      timestamp: Date.now()
    };

    setNotifications(prev => {
      const exists = prev.some(n => n.contactId === contactId && n.type === type);
      if (exists) return prev;
      return [...prev, notification];
    });

    setTimeout(() => {
      removeNotification(id);
    }, AUTO_REMOVE_MS);
  }, [removeNotification]);

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    removeByContactId
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
